import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEditAddress } from '@hooks/register/use-edit-address';
import { useLocalize } from '@hooks/use-localize';
import { editAddressSchema } from '@views/register/pwl/pwl-jug-sections/edit-address.schema';
import type { IEditAddressFormValues } from '@/types/history/edit-address';
import FieldError from '@components/field-error';

export type TAddressBanner = 'none' | 'notValid' | 'needsMoreInfo';

interface IProps {
    registrationId: number;
    editKey: string;
    banner: TAddressBanner;
    onClose: () => void;
    onSaved: () => void;
}

const EditAddressModal = ({ registrationId, editKey, banner, onClose, onSaved }: IProps) => {
    const { model, isLoading, error, submit, isSubmitting, submitError } = useEditAddress(registrationId, editKey);

    const t = useLocalize(model.languageData);

    const {
        register,
        handleSubmit,
        reset,
        setError,
        formState: { errors },
    } = useForm<IEditAddressFormValues>({
        resolver: zodResolver(editAddressSchema),
        defaultValues: { addressLine1: '', city: '', state: '', postalCode: '' },
    });

    // The form mounts before the fetch resolves, so seed it once the model arrives.
    useEffect(() => {
        if (isLoading) return;

        reset({
            addressLine1: model.addressLine1,
            city: model.city,
            state: model.state == null ? '' : String(model.state),
            postalCode: model.postalCode,
        });
    }, [isLoading, model.addressLine1, model.city, model.state, model.postalCode, reset]);

    const onValid = async (values: IEditAddressFormValues) => {
        const result = await submit(values);

        if (!result.ok) {
            Object.entries(result.errors ?? {}).forEach(([field, message]) => {
                setError(field as keyof IEditAddressFormValues, { type: 'server', message });
            });

            return;
        }

        onSaved();
        onClose();
    };

    return (
        <>
            <div className="modal fade in" tabIndex={-1} role="dialog" style={{ display: 'block' }}>
                <div className="modal-dialog" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <button
                                type="button"
                                className="close"
                                aria-label="Close"
                                onClick={onClose}
                            >
                                <span aria-hidden="true">&times;</span>
                            </button>
                            <h4 className="modal-title">
                                {t('DashLabel_EditAddress', 'Edit Address')}
                            </h4>
                        </div>

                        <form onSubmit={handleSubmit(onValid)}>
                            <div className="modal-body">
                                {banner === 'notValid' && (
                                    <div className="alert alert-danger">
                                        The address that you entered cannot be validated, please
                                        check your entry and click continue. Often this can be fixed
                                        by replacing a <span className="label label-default">#</span>{' '}
                                        with <span className="label label-default">APT</span> or{' '}
                                        <span className="label label-default">STE</span>.
                                    </div>
                                )}

                                {banner === 'needsMoreInfo' && (
                                    <div className="alert alert-danger">
                                        More information is needed in order to validate this address.
                                        Please enter more specific information such as apartment (
                                        <span className="label label-default">APT</span>) or suite
                                        number (<span className="label label-default">STE</span>) and
                                        click continue.
                                    </div>
                                )}

                                {error && <div className="alert alert-danger">{error}</div>}
                                {submitError && <div className="alert alert-danger">{submitError}</div>}

                                {isLoading ? (
                                    <div>Loading&hellip;</div>
                                ) : (
                                    <>
                                        <div className="form-group">
                                            <label className="control-label" htmlFor="AddressLine1">
                                                <FieldError message={errors.addressLine1?.message} />{' '}
                                                {t('DashLabel_StreetAddress', 'Street Address')}{' '}
                                                <span className="label label-default">no PO Box</span>:
                                            </label>
                                            <input
                                                id="AddressLine1"
                                                className="form-control"
                                                maxLength={64}
                                                {...register('addressLine1')}
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label className="control-label" htmlFor="City">
                                                <FieldError message={errors.city?.message} />{' '}
                                                {t('Word_City', 'City')}:
                                            </label>
                                            <input
                                                id="City"
                                                className="form-control"
                                                maxLength={32}
                                                {...register('city')}
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label className="control-label" htmlFor="State">
                                                <FieldError message={errors.state?.message} />{' '}
                                                {t('Word_State', 'State')}:
                                            </label>
                                            <select
                                                id="State"
                                                className="form-control"
                                                {...register('state')}
                                            >
                                                <option value="">
                                                    {t('DashLabel_PleaseSelect', 'Please Select')}
                                                </option>
                                                {model.stateOptions.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="form-group">
                                            <label className="control-label" htmlFor="PostalCode">
                                                <FieldError message={errors.postalCode?.message} />{' '}
                                                {t('DashLabel_PostalCode', 'Postal Code')}:
                                            </label>
                                            <input
                                                id="PostalCode"
                                                className="form-control"
                                                {...register('postalCode')}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={isLoading || isSubmitting}
                                >
                                    {t('Word_Continue', 'Continue')}
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-default"
                                    disabled={isSubmitting}
                                    onClick={onClose}
                                >
                                    {t('Word_Cancel', 'Cancel')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade in"></div>
        </>
    );
};

export default EditAddressModal;
